import { LONG_PRESS_MS } from "../bar/tips.ts";

/**
 * When a press becomes a drag (P2.8-T14; ADR-121 decision 3.1, which P2.8-T29 builds on): a press on a source that
 * moves more than `PRESS_SLOP_PX` CSS pixels before `LONG_PRESS_MS` has passed. A press released without moving that far
 * is a click; a press held still for `LONG_PRESS_MS` is a long press, which pins a tooltip (ADR-061's amendment of
 * 2026-10-02, item 3) and never drags, except a press that asks to be held first (`press`'s `holdFirst`), which drags
 * only once held and moved. The gesture knows nothing of what it carries or where it may land: a source is
 * a surface's name and an index in it, so the bag's slots are its first sources and targets and the bar's slots join
 * by the same anchors (`targets.ts`). It reads no DOM and allocates nothing per move.
 */

/**
 * How far a press may move, in CSS pixels, before it stops being a click or a long press and becomes a drag.
 */
export const PRESS_SLOP_PX = 8;

/**
 * A place a drag starts from or ends on: a surface (`bag`, and `bar` once P2.8-T29 adds it) and an index in it.
 */
export interface DragAnchor {
  readonly surface: string;
  readonly index: number;
}

/**
 * One press and the drag it may become.
 *
 * @example
 * ```ts
 * const gesture = new DragGesture();
 * gesture.press({ surface: "bag", index: 3 }, event.pointerId, event.clientX, event.clientY, performance.now());
 * if (gesture.move(event.pointerId, x, y, performance.now())) {
 *   bag.pick(3);
 * }
 * const source = gesture.release(event.pointerId);
 * ```
 */
export class DragGesture {
  private source: DragAnchor | null = null;
  private pointer = -1;
  private x = 0;
  private y = 0;
  private at = 0;
  private started = false;
  private holdFirst = false;

  /**
   * The source of the drag under way, or null while no press has become one.
   *
   * @returns The source.
   */
  public get active(): DragAnchor | null {
    return this.started ? this.source : null;
  }

  /**
   * Whether a press that waits to become a drag once held (`press`'s `holdFirst`) is under way.
   *
   * @returns True from such a press until it is released, cancelled or ended.
   */
  public get waitingToHold(): boolean {
    return this.source !== null && this.holdFirst;
  }

  /**
   * Starts watching a press on a source, forgetting any press before it.
   *
   * @param source Where the press began.
   * @param pointer The press's pointer id.
   * @param x Its client x in CSS pixels.
   * @param y Its client y in CSS pixels.
   * @param now The page clock in ms.
   * @param holdFirst Whether the press must be held still for `LONG_PRESS_MS` before it may drag, as a touch's is where a
   *   swipe means something else (the bag's tools carried to the belt's chip, ADR-142 decision 3b): a move past the slop
   *   before then ends the press, and one after it becomes the drag.
   */
  public press(source: DragAnchor, pointer: number, x: number, y: number, now: number, holdFirst = false): void {
    this.source = source;
    this.pointer = pointer;
    this.x = x;
    this.y = y;
    this.at = now;
    this.started = false;
    this.holdFirst = holdFirst;
  }

  /**
   * Hears the pointer move: the press becomes a drag the first time it is more than `PRESS_SLOP_PX` from where it began
   * while `LONG_PRESS_MS` has not passed. A press held past that without moving far enough is a long press and never
   * becomes a drag.
   *
   * @param pointer The pointer id.
   * @param x Its client x.
   * @param y Its client y.
   * @param now The page clock in ms.
   * @returns True only for the move that started the drag.
   */
  public move(pointer: number, x: number, y: number, now: number): boolean {
    if (this.source === null || pointer !== this.pointer || this.started) {
      return false;
    }
    const far = Math.hypot(x - this.x, y - this.y) > PRESS_SLOP_PX;
    const held = now - this.at >= LONG_PRESS_MS;
    if (this.holdFirst) {
      if (!far) {
        return false;
      }
      if (!held) {
        this.forget();
        return false;
      }
      this.started = true;
      return true;
    }
    if (held) {
      this.forget();
      return false;
    }
    if (!far) {
      return false;
    }
    this.started = true;
    return true;
  }

  /**
   * Ends the press as its pointer is released.
   *
   * @param pointer The pointer id.
   * @returns The source when the press had become a drag, so the caller drops it where the pointer is; null for a
   *   click, a long press or another pointer.
   */
  public release(pointer: number): DragAnchor | null {
    if (pointer !== this.pointer) {
      return null;
    }
    const dragged = this.active;
    this.forget();
    return dragged;
  }

  /**
   * Ends the press or the drag with nothing dropped, as Escape, a lost window or a cancelled pointer does.
   *
   * @returns Whether a drag was under way.
   */
  public cancel(): boolean {
    const dragging = this.started;
    this.forget();
    return dragging;
  }

  /**
   * Forgets the press.
   */
  private forget(): void {
    this.holdFirst = false;
    this.source = null;
    this.pointer = -1;
    this.started = false;
  }
}
